import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Clock, 
  Plus, 
  Search,
  Eye,
  Trash2,
  Loader2,
  CalendarIcon,
  User,
  Settings,
  Edit
} from 'lucide-react';
import { formatDateDisplay } from '../../utils/dateUtils';

const attendanceOptions = ['In-person Only', 'Remote Only', 'Attendee Choice'];

const ScheduleSetsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSchoolAdmin = user?.role === 'school_admin';
  const [scheduleSets, setScheduleSets] = useState([]);
  const [schoolLevels, setSchoolLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [date, setDate] = useState(new Date());
  const [createMode, setCreateMode] = useState('generated'); // 'generated' or 'custom'
  
  // Generated schedule state
  const [newSet, setNewSet] = useState({
    friendly_name: '',
    conference_date: format(new Date(), 'yyyy-MM-dd'),
    school_level_id: 0,
    attendance_option: 'In-person Only',
    start_time: '08:00',
    end_time: '17:00',
    duration_minutes: 15,
    break_minutes: 5
  });

  // Custom schedule state
  const [customSet, setCustomSet] = useState({
    friendly_name: '',
    conference_date: format(new Date(), 'yyyy-MM-dd'),
    school_level_id: 0,
    attendance_option: 'In-person Only',
    num_slots: 5
  });
  const [customSlots, setCustomSlots] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schedRes, userRes] = await Promise.all([
        api.get('/schedule-sets'),
        api.get('/auth/me')
      ]);
      
      const levelsRes = await api.get(`/schools/${userRes.data.school_id}/levels`);
      
      setScheduleSets(schedRes.data);
      setSchoolLevels(levelsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newSet.friendly_name) {
      toast.error('Please enter a name');
      return;
    }

    setFormLoading(true);
    try {
      const response = await api.post('/schedule-sets', newSet);
      toast.success('Schedule set created with time slots');
      setCreateDialogOpen(false);
      navigate(`/schedule-sets/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create');
    } finally {
      setFormLoading(false);
    }
  };

  const generateCustomSlots = () => {
    const numSlots = customSet.num_slots || 5;
    const slots = [];
    
    for (let i = 0; i < numSlots; i++) {
      slots.push({
        id: `temp-${i}`,
        start_time: '',
        end_time: ''
      });
    }
    
    setCustomSlots(slots);
  };

  const updateCustomSlot = (index, field, value) => {
    const updated = [...customSlots];
    updated[index] = { ...updated[index], [field]: value };
    setCustomSlots(updated);
  };

  const deleteCustomSlot = (index) => {
    const updated = customSlots.filter((_, i) => i !== index);
    setCustomSlots(updated);
  };

  const addCustomSlot = () => {
    setCustomSlots([...customSlots, {
      id: `temp-${Date.now()}`,
      start_time: '',
      end_time: ''
    }]);
  };

  const handleCreateCustom = async () => {
    if (!customSet.friendly_name) {
      toast.error('Please enter a name');
      return;
    }
    
    if (customSlots.length === 0) {
      toast.error('Please add at least one time slot');
      return;
    }

    // Validate slots
    for (let i = 0; i < customSlots.length; i++) {
      const slot = customSlots[i];
      if (!slot.start_time || !slot.end_time) {
        toast.error(`Slot ${i + 1} is missing start or end time`);
        return;
      }
      if (slot.start_time >= slot.end_time) {
        toast.error(`Slot ${i + 1}: End time must be after start time`);
        return;
      }
    }

    setFormLoading(true);
    try {
      const payload = {
        friendly_name: customSet.friendly_name,
        conference_date: customSet.conference_date,
        school_level_id: customSet.school_level_id,
        attendance_option: customSet.attendance_option,
        time_slots: customSlots.map(s => ({
          start_time: s.start_time,
          end_time: s.end_time
        }))
      };
      
      const response = await api.post('/schedule-sets/custom', payload);
      toast.success('Custom schedule set created');
      setCreateDialogOpen(false);
      navigate(`/schedule-sets/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create custom schedule');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this schedule set?')) return;
    
    try {
      await api.delete(`/schedule-sets/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleDateSelect = (selectedDate) => {
    if (selectedDate) {
      setDate(selectedDate);
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      if (createMode === 'generated') {
        setNewSet({ ...newSet, conference_date: dateStr });
      } else {
        setCustomSet({ ...customSet, conference_date: dateStr });
      }
    }
  };

  const resetDialogState = () => {
    setCreateMode('generated');
    setNewSet({
      friendly_name: '',
      conference_date: format(new Date(), 'yyyy-MM-dd'),
      school_level_id: 0,
      attendance_option: 'In-person Only',
      start_time: '08:00',
      end_time: '17:00',
      duration_minutes: 15,
      break_minutes: 5
    });
    setCustomSet({
      friendly_name: '',
      conference_date: format(new Date(), 'yyyy-MM-dd'),
      school_level_id: 0,
      attendance_option: 'In-person Only',
      num_slots: 5
    });
    setCustomSlots([]);
    setDate(new Date());
  };

  const openCreateDialog = () => {
    resetDialogState();
    setCreateDialogOpen(true);
  };

  const filteredSets = scheduleSets.filter(ss =>
    ss.friendly_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-outfit text-3xl font-bold text-slate-900">Schedule Sets</h1>
            <p className="text-slate-500">Manage conference time slot templates</p>
          </div>
          <Button 
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={openCreateDialog}
            data-testid="create-schedule-set-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Schedule Set
          </Button>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-outfit">Your Schedule Sets</CardTitle>
                <CardDescription>{scheduleSets.length} schedule sets created</CardDescription>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-schedule-sets"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredSets.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                {searchTerm ? 'No schedule sets match your search' : 'No schedule sets yet'}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Time Range</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Slots</TableHead>
                    <TableHead>Used By</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSets.map((ss) => (
                    <TableRow key={ss.id} data-testid={`sched-set-${ss.id}`}>
                      <TableCell className="font-medium">{ss.friendly_name}</TableCell>
                      <TableCell>{formatDateDisplay(ss.conference_date)}</TableCell>
                      <TableCell>{ss.start_time} - {ss.end_time}</TableCell>
                      <TableCell>{ss.school_level_description || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {ss.time_slots?.filter(s => s.is_active).length || 0} active
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {ss.linked_conference_sets_count > 0 ? (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 px-2">
                                <Badge variant="outline" className="cursor-pointer">
                                  {ss.linked_conference_sets_count} conference{ss.linked_conference_sets_count > 1 ? 's' : ''}
                                </Badge>
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-60 p-2">
                              <p className="text-sm font-medium mb-2">Used by:</p>
                              <div className="space-y-1">
                                {ss.linked_conference_sets?.map((cs) => (
                                  <div key={cs.id} className="text-sm text-slate-600 flex items-center gap-2">
                                    <User className="h-3 w-3" />
                                    {cs.friendly_name}
                                  </div>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        ) : (
                          <span className="text-slate-400 text-sm">Not used</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/schedule-sets/${ss.id}`)}
                            data-testid={`view-sched-set-${ss.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-rose-600"
                            onClick={() => handleDelete(ss.id)}
                            data-testid={`delete-sched-set-${ss.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-outfit">Create Schedule Set</DialogTitle>
              <DialogDescription>
                Define time slots for parent-teacher conferences
              </DialogDescription>
            </DialogHeader>
            
            <Tabs value={createMode} onValueChange={setCreateMode} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="generated" data-testid="tab-generated">
                  <Clock className="h-4 w-4 mr-2" />
                  Generated
                </TabsTrigger>
                <TabsTrigger value="custom" data-testid="tab-custom">
                  <Settings className="h-4 w-4 mr-2" />
                  Custom
                </TabsTrigger>
              </TabsList>
              
              {/* Generated Schedule Tab */}
              <TabsContent value="generated" className="space-y-4 mt-4">
                <p className="text-sm text-slate-500">
                  Automatically generate time slots based on start/end times, duration, and break intervals.
                </p>
                
                <div className="space-y-2">
                  <Label>Friendly Name *</Label>
                  <Input
                    value={newSet.friendly_name}
                    onChange={(e) => setNewSet({ ...newSet, friendly_name: e.target.value })}
                    placeholder="Morning Sessions - Oct 15"
                    data-testid="sched-set-name-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Conference Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start" data-testid="sched-date-picker">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formatDateDisplay(newSet.conference_date)}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={handleDateSelect}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>School Level</Label>
                    <Select
                      value={String(newSet.school_level_id)}
                      onValueChange={(v) => setNewSet({ ...newSet, school_level_id: parseInt(v) })}
                    >
                      <SelectTrigger data-testid="sched-level-select">
                        <SelectValue placeholder="Select level" />
                      </SelectTrigger>
                      <SelectContent>
                        {schoolLevels.map((level) => (
                          <SelectItem key={level.level_id} value={String(level.level_id)}>
                            {level.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Default Attendee Option</Label>
                  <Select
                    value={newSet.attendance_option}
                    onValueChange={(v) => setNewSet({ ...newSet, attendance_option: v })}
                  >
                    <SelectTrigger data-testid="sched-attendance-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {attendanceOptions.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-slate-500">
                    You will be able to make one-off changes for particular teachers during the review of the Export Set that is created.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Time</Label>
                    <Input
                      type="time"
                      value={newSet.start_time}
                      onChange={(e) => setNewSet({ ...newSet, start_time: e.target.value })}
                      data-testid="sched-start-time"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Time</Label>
                    <Input
                      type="time"
                      value={newSet.end_time}
                      onChange={(e) => setNewSet({ ...newSet, end_time: e.target.value })}
                      data-testid="sched-end-time"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Duration (minutes)</Label>
                    <Input
                      type="number"
                      value={newSet.duration_minutes}
                      onChange={(e) => setNewSet({ ...newSet, duration_minutes: parseInt(e.target.value) })}
                      min={5}
                      max={60}
                      data-testid="sched-duration"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Break Between (minutes)</Label>
                    <Input
                      type="number"
                      value={newSet.break_minutes}
                      onChange={(e) => setNewSet({ ...newSet, break_minutes: parseInt(e.target.value) })}
                      min={0}
                      max={30}
                      data-testid="sched-break"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Custom Schedule Tab */}
              <TabsContent value="custom" className="space-y-4 mt-4">
                <p className="text-sm text-slate-500">
                  Create a custom schedule with individually defined time slots for irregular conference schedules.
                </p>
                
                <div className="space-y-2">
                  <Label>Friendly Name *</Label>
                  <Input
                    value={customSet.friendly_name}
                    onChange={(e) => setCustomSet({ ...customSet, friendly_name: e.target.value })}
                    placeholder="Custom Sessions - Special Event"
                    data-testid="custom-sched-name-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Conference Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start" data-testid="custom-date-picker">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formatDateDisplay(customSet.conference_date)}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={handleDateSelect}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>School Level</Label>
                    <Select
                      value={String(customSet.school_level_id)}
                      onValueChange={(v) => setCustomSet({ ...customSet, school_level_id: parseInt(v) })}
                    >
                      <SelectTrigger data-testid="custom-level-select">
                        <SelectValue placeholder="Select level" />
                      </SelectTrigger>
                      <SelectContent>
                        {schoolLevels.map((level) => (
                          <SelectItem key={level.level_id} value={String(level.level_id)}>
                            {level.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Default Attendee Option</Label>
                  <Select
                    value={customSet.attendance_option}
                    onValueChange={(v) => setCustomSet({ ...customSet, attendance_option: v })}
                  >
                    <SelectTrigger data-testid="custom-attendance-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {attendanceOptions.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Generate Initial Slots */}
                <div className="border rounded-lg p-4 bg-slate-50">
                  <div className="flex items-center justify-between mb-3">
                    <Label>Generate Starting Slots</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={customSet.num_slots}
                        onChange={(e) => setCustomSet({ ...customSet, num_slots: parseInt(e.target.value) || 5 })}
                        min={1}
                        max={50}
                        className="w-20"
                        data-testid="custom-num-slots"
                      />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={generateCustomSlots}
                        data-testid="generate-slots-btn"
                      >
                        Generate
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">
                    Generate initial time slots as a starting point, then edit them below.
                  </p>
                </div>

                {/* Time Slots Table */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Time Slots ({customSlots.length})</Label>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={addCustomSlot}
                      data-testid="add-slot-btn"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Slot
                    </Button>
                  </div>
                  
                  {customSlots.length === 0 ? (
                    <div className="border rounded-lg p-6 text-center text-slate-500">
                      <Clock className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                      <p>No time slots yet.</p>
                      <p className="text-sm">Generate slots above or add them manually.</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">#</TableHead>
                            <TableHead>Start Time</TableHead>
                            <TableHead>End Time</TableHead>
                            <TableHead className="w-16 text-right">Delete</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {customSlots.map((slot, index) => (
                            <TableRow key={slot.id}>
                              <TableCell className="font-medium text-slate-500">
                                {index + 1}
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="time"
                                  value={slot.start_time}
                                  onChange={(e) => updateCustomSlot(index, 'start_time', e.target.value)}
                                  className="w-32"
                                  data-testid={`slot-start-${index}`}
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="time"
                                  value={slot.end_time}
                                  onChange={(e) => updateCustomSlot(index, 'end_time', e.target.value)}
                                  className="w-32"
                                  data-testid={`slot-end-${index}`}
                                />
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-rose-600 hover:text-rose-700"
                                  onClick={() => deleteCustomSlot(index)}
                                  data-testid={`delete-slot-${index}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              {createMode === 'generated' ? (
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleCreate}
                  disabled={formLoading}
                  data-testid="submit-create-sched-set"
                >
                  {formLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create & Generate Slots'}
                </Button>
              ) : (
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleCreateCustom}
                  disabled={formLoading || customSlots.length === 0}
                  data-testid="submit-create-custom-sched"
                >
                  {formLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : `Create with ${customSlots.length} Slots`}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ScheduleSetsPage;
